import Image from "next/image";
import Link from "next/link";
import slug from "slugify";
import { FaChevronRight, FaMapMarkerAlt } from "react-icons/fa";
import { LuCalendar } from "react-icons/lu";

import Button from "./form/button";
import { formatCurrency, formatMonthlyCost } from "@utils/format";
import { DEFAULT_CASH_DEPOSIT, DEFAULT_INTEREST_RATE, DEFAULT_MONTHS, DEFAULT_SALVAGE_AMOUNT } from "@utils/constants";
import { CarData } from "@src/types/car";
import { getFacilityBySlug } from "@/data/facilities";
import { GearboxIcon, OdometerIcon } from "./icons";

const CarCard = ({ car }: { car: CarData }) => {
	const images = car?.images;
	const { name, gearBox, modelYear, milage, price } = car;
	const city = car.facilitySlug ? getFacilityBySlug(car.facilitySlug)?.name : undefined;

	return (
		<Link
			className="group w-full h-full bg-surface/80 hover:border-white/15 shadow-sm hover:shadow-xl hover:shadow-black/40 hover:-translate-y-1 transition-all duration-300 cursor-pointer overflow-hidden"
			href={`/fordon/${slug(name, {
				trim: true,
				strict: true,
				lower: true,
			})}`}
			title={name}
		>
			{/* Image with hover zoom */}
			<figure className="block h-auto overflow-hidden relative">
				<Image
					priority
					src={images[0]?.imageFormats[1].url}
					alt={car?.modelRaw ?? ""}
					width={1280}
					height={768}
					sizes="100vw"
					className="object-cover w-full h-auto group-hover:scale-105 transition-transform duration-500 ease-out"
				/>
				{/* Price badge overlay */}
				<div className="absolute bottom-0 right-0 bg-black/70 backdrop-blur-sm px-4 py-2">
					<p className="text-lg font-bold text-white mb-0">{formatCurrency(price.value)}</p>
					<p className="text-xs text-gray-300 mb-0">
						{formatMonthlyCost(
							price.value,
							DEFAULT_MONTHS,
							DEFAULT_INTEREST_RATE,
							DEFAULT_CASH_DEPOSIT,
							DEFAULT_SALVAGE_AMOUNT
						)}
					</p>
				</div>
			</figure>

			<div className="p-5">
				{/* Title */}
				<h3 className="text-base font-semibold group-hover:text-accent transition-colors duration-300 mb-1">{name}</h3>

				{/* City */}
				{city && (
					<p className="flex items-center gap-1.5 text-xs text-white mb-3">
						<FaMapMarkerAlt className="" size={12} /> {city}
					</p>
				)}

				{/* Specs row with dividers */}
				<div className="flex items-center text-sm text-gray-400 divide-x divide-white/10">
					<p className="flex items-center gap-1.5 pr-3">
						<LuCalendar size={14} /> {modelYear}
					</p>
					<p className="flex items-center gap-1.5 px-3">
						<OdometerIcon size={14} /> {milage} mil
					</p>
					<p className="flex items-center gap-1.5 pl-3">
						<GearboxIcon size={14} /> {gearBox}
					</p>
				</div>

				{/* CTA */}
				<div className="flex-1 items-end mt-4">
					<Button className="self-end w-full">
						Läs mer <FaChevronRight />
					</Button>
				</div>
			</div>
		</Link>
	);
};

const convertGearboxType = (type: string, articleType: string) => {
	switch (type) {
		case "AUTOMATIC":
			return "Automat";
		case "MANUAL":
			return "Manuell";
		default:
			if (articleType === "MOTORCYCLE") return "Manuell";
			return "UNKNOWN";
	}
};

const convertBodyType = (type: string) => {
	switch (type) {
		case "SEDAN":
			return "Sedan";
		case "KOMBI":
			return "Kombi";
		case "COUPE":
			return "Kupé";
		case "SPORTCOUPE":
			return "Sportkupé";
		case "HATCHBACK":
			return "Halvkombi";
		case "CABRIOLET":
			return "Cabriolet";
		case "MINIBUS":
			return "Minibuss";
		case "SUV":
			return "SUV";
		default:
			return "?";
	}
};

const convertDriveWheels = (type: string) => {
	switch (type) {
		case "FOURWHEEL":
			return "Fyrhjulsdriven";
		case "REARWHEEL":
			return "Bakhjulsdriven";
		case "FRONTWHEEL":
			return "Framhjulsdriven";
		default:
			return "?";
	}
};
const formatFuel = (primary, secondary) => {
	if (primary && secondary !== "UNKNOWN") return "Hybrid";
	else {
		switch (primary) {
			case "BENSIN":
				return "Bensin";
			case "DIESEL":
				return "Diesel";
			case "ELECTRIC":
				return "El";
			case "HYBRID":
				return "Hybrid";
			default:
				return "Okänd";
		}
	}
};

export default CarCard;
